<template>
  <v-sheet class="py-4" elevation="2" rounded>
    <div class="px-6 mb-2 d-flex align-center justify-space-between">
      <p
        data-testid="home-text-routinesTitle"
        class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 font-weight-700"
      >
        {{ $t('home.routines.title') }}
      </p>

      <Button
        v-if="routinePages.length > 0"
        plain
        color="primary"
        size="small"
        data-testid="home-button-allRoutines"
        :text="$t('home.routines.go_to_routines_button')"
        :to="{ name: 'routineDefinitions' }"
        @click="
          trackHomeNavigate({
            section: 'allRoutines',
            destination: 'routines',
          })
        "
      />
    </div>

    <EmptyState
      v-if="!isLoading && routines.length === 0"
      icon="mdi-clipboard-text-outline"
      :title="$t('home.routines.empty_state_title')"
      :message="$t('home.routines.empty_state_message')"
      :buttonTestId="'home-button-createRoutineWidget'"
      :buttonText="
        routineDefinitionsPermissions.create
          ? $t('routines.create_routine_title')
          : null
      "
      @click="navigateToRoutines"
    />

    <v-row v-if="isLoading" class="px-6">
      <v-col
        v-for="item in 2"
        class="my-2"
        lg="6"
        md="12"
        :key="`skeleton-${item}`"
      >
        <RoutineCardSkeleton />
      </v-col>
    </v-row>

    <Carousel v-if="routinePages.length > 0" height="190">
      <!-- On small screens we should generate the carousel's pages using individual Routines instead of chunks of 2 elements. -->
      <template v-if="$vuetify.breakpoint.mobile">
        <v-carousel-item v-for="routine in routines" :key="routine.id">
          <v-row class="mt-1 px-8" align="start" justify="start">
            <v-col lg="6" md="12">
              <RoutineCard
                :routine="routine"
                @start="startRoutine(routine, 'routines')"
              />
            </v-col>
          </v-row>
        </v-carousel-item>
      </template>

      <!-- On big screens we should use chunks of 2 elements to generate the carousel's pages -->
      <template v-else>
        <v-carousel-item
          v-for="(page, index) in routinePages"
          :key="`routinesPage-${index}`"
        >
          <v-row class="h-100 mt-0 pt-2 px-6" align="start" justify="start">
            <v-col v-for="routine in page" lg="6" md="12" :key="routine.id">
              <RoutineCard
                :routine="routine"
                @start="startRoutine(routine, 'routines')"
              />
            </v-col>
          </v-row>
        </v-carousel-item>
      </template>
    </Carousel>

    <RoutineStartModal
      v-if="isStartModalOpen"
      :open="isStartModalOpen"
      :routine="selectedRoutine"
      @cancel="isStartModalOpen = false"
      @started="
        navigateTo({
          name: 'routineExecutionDetail',
          params: { routineId: $event },
        })
      "
    />
  </v-sheet>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import Carousel from '@/components/UI/Carousel.vue'
import RoutineStartModal from '@/components/RoutineStartModal.vue'

import EmptyState from '@/components/EmptyState.vue'
import RoutineCardSkeleton from './RoutineCardSkeleton.vue'
import RoutineCard from './RoutineCard.vue'

import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

export default {
  name: 'Routines',

  mixins: [mixin, trackingMixin, routineDefinitionPermissions],

  components: {
    Button,
    Carousel,
    EmptyState,
    RoutineCardSkeleton,
    RoutineStartModal,
    RoutineCard,
  },

  data() {
    return {
      isLoading: false,
      isShowingCachedSnapshot: false,
      isStartModalOpen: false,
      routinesFetchTimer: null,
      routines: [],
      selectedRoutine: null,
      routinePages: [],
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    routineDefinitionsPermissions() {
      return this.getRoutineDefinitionPermissions({})
    },
  },

  created() {
    this.hydrateFromSnapshot()
    this.routinesFetchTimer = setTimeout(
      this.fetchRoutines,
      HOME_WIDGET_FETCH_DELAYS.routines
    )
  },

  beforeDestroy() {
    clearTimeout(this.routinesFetchTimer)
  },

  methods: {
    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot'](
        'routines'
      )

      if (!_.isArray(snapshot?.routines)) {
        this.isLoading = true

        return
      }

      this.routines = [...snapshot.routines]
      this.routinePages = _.chunk(this.routines, 2)
      this.isShowingCachedSnapshot = true
    },

    saveSnapshot() {
      this.$store.commit('homeSnapshots/SET_ROUTINES_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        routines: [...this.routines],
      })
    },

    async fetchRoutines() {
      try {
        if (!this.isShowingCachedSnapshot) this.isLoading = true

        const filters = {
          isExecutableByMe: true,
          isGloballyAssigned: false,
          isActive: true,
        }

        const response = await api.routine.getStreamRoutineDefinition({
          organizationId: this.organizationId,
          filters,
        })

        this.routines = response.data.results.map(
          routineDefinition => routineDefinition.result
        )

        this.routinePages = _.chunk(this.routines, 2)

        this.saveSnapshot()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.isShowingCachedSnapshot = false
      }
    },

    navigateToRoutines() {
      this.navigateTo({ name: 'createRoutineDefinition' })
    },

    getFrequencyLabel(frequency) {
      return this.$t(
        this.$constants.routines.frequencies.find(f => f.value === frequency)
          ?.name
      )
    },

    startRoutine(routine, section) {
      this.selectedRoutine = routine
      this.isStartModalOpen = true
      this.trackHomeNavigate({
        section,
        element: 'startRoutine',
      })
    },
  },
}
</script>
